/** Find Edges — Sobel-based edge detection rendered as black edges
 *  on white (matches Photoshop's Find Edges output).
 *
 *  Sobel uses two 3×3 convolutions that approximate the partial
 *  derivatives of the image luminance along x and y. The magnitude
 *  `sqrt(Gx² + Gy²)` peaks at sharp luminance transitions and falls
 *  off in flat regions. We invert the result so flat regions render
 *  white and edges render as dark traces.
 *
 *  Operates on BT.601 luminance, not per-channel — this is closer to
 *  what the eye perceives as an "edge" and avoids the channel-
 *  separation artifacts running Sobel three times would create
 *  (e.g. red text on a green background would produce two separate
 *  edge traces instead of one). */

import { BT601_LUMA_B, BT601_LUMA_G, BT601_LUMA_R } from '../color-constants';

export interface FindEdgesValue {
  /** Sensitivity multiplier (0..200). 0 = identity, 100 = a "normal"
   *  Sobel pass, higher values amplify weak gradients. */
  amount: number;
}

export const identityFindEdgesValue = (): FindEdgesValue => ({ amount: 0 });

export function applyFindEdgesPixels(source: ImageData, value: FindEdgesValue): ImageData {
  const w = source.width;
  const h = source.height;
  const amount = Math.max(0, value.amount);
  if (amount === 0) {
    return new ImageData(new Uint8ClampedArray(source.data), w, h);
  }
  const k = amount / 100;
  const src = source.data;
  const out = new Uint8ClampedArray(src.length);
  const maxX = w - 1;
  const maxY = h - 1;

  // Pre-compute luminance into a Float32 buffer so the Sobel inner
  // loop reads from a single channel (faster + avoids redoing the
  // luminance math at every kernel tap).
  const n = w * h;
  const lum = new Float32Array(n);
  for (let i = 0, p = 0; p < n; i += 4, p++) {
    lum[p] = BT601_LUMA_R * src[i] + BT601_LUMA_G * src[i + 1] + BT601_LUMA_B * src[i + 2];
  }

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      // Sample 3×3 luminance neighborhood, clamping at edges.
      const x0 = x === 0 ? 0 : x - 1;
      const x1 = x;
      const x2 = x === maxX ? maxX : x + 1;
      const y0 = y === 0 ? 0 : y - 1;
      const y1 = y;
      const y2 = y === maxY ? maxY : y + 1;
      const a = lum[y0 * w + x0];
      const b = lum[y0 * w + x1];
      const c = lum[y0 * w + x2];
      const d = lum[y1 * w + x0];
      // const e = lum[y1 * w + x1]; // center; Sobel kernels have 0 weight here
      const f = lum[y1 * w + x2];
      const g = lum[y2 * w + x0];
      const hh = lum[y2 * w + x1];
      const ii = lum[y2 * w + x2];
      // Sobel kernels (Gx weights left col -1, right col +1 with the
      // middle row doubled; Gy weights top row -1, bottom row +1
      // with the middle col doubled).
      const gx = -a - 2 * d - g + c + 2 * f + ii;
      const gy = -a - 2 * b - c + g + 2 * hh + ii;
      const mag = Math.sqrt(gx * gx + gy * gy);
      // Scale and invert: 0 magnitude → white (255), large magnitude
      // → dark. The `k` multiplier amplifies subtle edges.
      const e = 255 - k * mag;
      const oi = (y * w + x) * 4;
      // Grayscale output across R/G/B; Uint8ClampedArray clamps.
      out[oi] = e;
      out[oi + 1] = e;
      out[oi + 2] = e;
      out[oi + 3] = src[oi + 3];
    }
  }
  return new ImageData(out, w, h);
}

export function applyFindEdgesToCanvas(
  source: ImageData,
  target: HTMLCanvasElement,
  value: FindEdgesValue,
): void {
  const tCtx = target.getContext('2d');
  if (!tCtx) return;
  if (target.width !== source.width || target.height !== source.height) {
    target.width = source.width;
    target.height = source.height;
  }
  tCtx.putImageData(applyFindEdgesPixels(source, value), 0, 0);
}
